
import React, { JSX, useRef, useState } from 'react'
import classNames from 'classnames'
import { ArrowUpTrayIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { AssetPickerModal } from 'components/AssetPickerModal'
import { useAuth } from 'context/user'
import { graphqlRequest } from 'lib/helpers'
import { AssetSummary, ImageUploadUrl } from 'types/api'
import requestImageUploadUrlMutation from 'graphql/requestImageUploadUrl.graphql'
import confirmAssetUploadMutation from 'graphql/confirmAssetUpload.graphql'

export const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']

export const MAX_IMAGE_BYTES = 8 * 1024 * 1024

export const assetS3KeyFromUpload = (upload: ImageUploadUrl): string => {
  if (upload.s3Key !== undefined) return upload.s3Key
  return new URL(upload.publicUrl).pathname.replace(/^\//, '')
}

const formatBytes = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(1)} MB`

export const imageFileProblem = (file: File): string | null => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'Images must be PNG, JPEG, WebP, or GIF.'
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return `Images must be ${formatBytes(MAX_IMAGE_BYTES)} or smaller.`
  }
  return null
}

export const uploadImageAsset = async (
  file: File,
  auth0Token: string
): Promise<{ upload: ImageUploadUrl, asset: Omit<AssetSummary, 'references'> | null }> => {
  const { upload } = await graphqlRequest<{ upload: ImageUploadUrl }>(
    requestImageUploadUrlMutation,
    { contentType: file.type, contentLength: file.size },
    auth0Token
  )

  const res = await fetch(upload.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`S3 upload failed: ${res.status} ${detail.slice(0, 200)}`)
  }

  const { asset } = await graphqlRequest<{ asset: Omit<AssetSummary, 'references'> | null }>(
    confirmAssetUploadMutation,
    { s3Key: assetS3KeyFromUpload(upload), fileName: file.name },
    auth0Token
  )
  return { upload, asset }
}

export const ImageUpload = ({
  value,
  onChange,
  shape = 'square',
  helpText,
  label = 'Image (optional)'
}: {
  value: string
  onChange: (next: string) => void
  kind?: string
  shape?: 'square' | 'circle'
  helpText?: string
  label?: string | null
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  const handlePick = (): void => {
    fileInputRef.current?.click()
  }

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if ((file == null) || auth0Token === undefined) return

    const problem = imageFileProblem(file)
    if (problem !== null) {
      setError(problem)
      return
    }

    setUploading(true)
    setError(null)
    try {
      const { upload } = await uploadImageAsset(file, auth0Token)
      onChange(upload.publicUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleClear = (): void => {
    onChange('')
    setError(null)
  }

  const thumbClasses = classNames(
    'flex size-16 shrink-0 items-center justify-center overflow-hidden border',
    shape === 'circle' ? 'rounded-full' : 'rounded-md',
    value.length > 0 ? 'border-white/10' : 'border-dashed border-white/10 bg-dm-bg-accent/40'
  )

  return (
    <div>
      {label !== null && (
        <span className='mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
          {label}
        </span>
      )}
      <div className='flex items-start gap-3'>
        <div className={thumbClasses}>
          {value.length > 0
            ? <img
                src={value}
                alt='preview'
                className='size-full object-cover'
                onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2' }}
              />
            : <ArrowUpTrayIcon className='size-5 text-dm-text-accent' aria-hidden='true' />}
        </div>

        <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
          <div className='flex flex-wrap items-center gap-2'>
            <input
              ref={fileInputRef}
              type='file'
              accept={ALLOWED_IMAGE_TYPES.join(',')}
              onChange={(e) => { void handleFile(e) }}
              className='hidden'
            />
            <button
              type='button'
              onClick={handlePick}
              disabled={uploading}
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {uploading
                ? <><LoadingSpinner /> Uploading…</>
                : <><ArrowUpTrayIcon className='size-3.5' aria-hidden='true' /> {value.length > 0 ? 'Replace' : 'Upload'}</>}
            </button>
            <button
              type='button'
              onClick={() => setPickerOpen(true)}
              disabled={uploading}
              data-testid='image-upload-select-from-uploads'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              <PhotoIcon className='size-3.5' aria-hidden='true' />
              Select from uploads
            </button>
            {value.length > 0 && !uploading && (
              <button
                type='button'
                onClick={handleClear}
                title='Remove image'
                className='inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1.5 text-xs text-dm-text-secondary hover:border-red-500/40 hover:text-red-300'
              >
                <XMarkIcon className='size-3.5' aria-hidden='true' />
                Clear
              </button>
            )}
          </div>
          {value.length > 0 && (
            <code className='block truncate rounded bg-dm-bg-accent/40 px-2 py-1 font-mono text-[11px] text-dm-text-accent'>
              {value}
            </code>
          )}
          {error !== null && (
            <span className='text-[11px] text-red-300'>{error}</span>
          )}
          <p className='text-[11px] text-dm-text-accent'>
            {helpText ?? `≤ ${formatBytes(MAX_IMAGE_BYTES)}. PNG, JPEG, WebP, or GIF.`}
          </p>
        </div>
      </div>

      {pickerOpen && (
        <AssetPickerModal
          onClose={() => setPickerOpen(false)}
          onSelect={(publicUrl) => {
            onChange(publicUrl)
            setPickerOpen(false)
          }}
        />
      )}
    </div>
  )
}
